import { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  Mail, 
  Clock, 
  Send,
  CheckCircle,
  Globe,
  Shield,
  Phone,
  MapPin,
  Lock
} from 'lucide-react'

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  })
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const encode = (data: Record<string, string>) => {
    return Object.keys(data)
      .map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]))
      .join('&')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode({ 'form-name': 'contact', ...formData }),
      })
      setIsSubmitted(true)
      setFormData({ name: '', email: '', phone: '', company: '', subject: '', message: '' })
      setTimeout(() => setIsSubmitted(false), 5000)
    } catch (error) {
      console.error('Form submit error', error)
    }
  }

  const languages = [
    { name: 'Polski', flag: '🇵🇱' },
    { name: 'Deutsch', flag: '🇩🇪' },
    { name: 'English', flag: '🇬🇧' }
  ]

  const metrics = [
    {
      icon: <Clock className="w-5 h-5" />,
      title: "Czas Odpowiedzi",
      value: "Do 24h",
      desc: "Zazwyczaj odpowiadamy w ciągu kilku godzin."
    },
    {
      icon: <Globe className="w-5 h-5" />,
      title: "Obszar Spedycji",
      value: "PL / CH / DE",
      desc: "Regularne przejazdy Polska - Szwajcaria - Niemcy."
    },
    {
      icon: <Shield className="w-5 h-5" />,
      title: "Ubezpieczenie OCP",
      value: "Pełne",
      desc: "Ładunki ubezpieczone na czas transportu."
    }
  ]

  return (
    <div className="min-h-screen bg-slate-50/50 overflow-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-900 via-primary-850 to-primary-950 text-white py-24 sm:py-32">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(2,132,199,0.1),transparent_50%)] pointer-events-none"></div>
        <div className="container-max px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto space-y-6"
          >
            <span className="text-xs font-extrabold text-secondary-300 uppercase tracking-widest bg-white/5 border border-white/10 py-1.5 px-4 rounded-full">Porozmawiajmy o współpracy</span>
            <h1 className="text-4xl md:text-6xl font-extrabold font-display tracking-tight leading-tight">
              Kontakt
            </h1>
            <p className="text-lg md:text-xl text-primary-200/90 leading-relaxed font-light">
              Niezależnie od tego, czy potrzebujesz transportu towaru, czy udostępnienia certyfikatu CKZ – jesteśmy do Twojej dyspozycji.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Metrics Section */}
      <section className="py-12 bg-white border-b border-slate-100 relative">
        <div className="container-max px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {metrics.map((m, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                viewport={{ once: true }}
                className="flex items-center space-x-4 p-4"
              >
                <div className="bg-primary-50 p-3 rounded-2xl text-primary-600 shrink-0 shadow-sm">
                  {m.icon}
                </div>
                <div>
                  <h4 className="text-xs font-extrabold uppercase tracking-wider text-slate-400 leading-none">{m.title}</h4>
                  <div className="text-lg font-bold text-slate-900 mt-1">{m.value}</div>
                  <p className="text-xs text-slate-500 mt-0.5 leading-normal">{m.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Dual Panel Contact Section */}
      <section className="section-padding bg-slate-50/30">
        <div className="container-max px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-stretch">
            {/* Left Panel - Brand Cards & Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="lg:col-span-5 flex flex-col justify-between"
            >
              <div className="bg-gradient-to-br from-primary-900 to-primary-950 text-white rounded-3xl p-8 md:p-10 shadow-xl relative overflow-hidden flex-1 flex flex-col justify-between gap-8">
                {/* Decorative glow */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-secondary-500/15 rounded-full blur-2xl"></div>
                
                <div className="space-y-6">
                  <h3 className="text-2xl font-bold font-display text-white">FG Falke</h3>
                  <p className="text-sm text-primary-200/90 leading-relaxed font-light">
                    Siedziba naszej firmy znajduje się w Małopolsce, lecz nasze ciężarówki i kierowcy codziennie pokonują trasy spedycyjne w Niemczech i Szwajcarii.
                  </p>
                  
                  {/* Real Contact Coordinates */}
                  <div className="space-y-5 pt-4 border-t border-white/10">
                    <a href="tel:+41762373301" className="flex items-center space-x-3.5 group text-sm text-primary-200 hover:text-white transition-colors">
                      <div className="bg-white/5 p-2.5 rounded-xl border border-white/10 group-hover:bg-white/10 shrink-0">
                        <Phone className="w-5 h-5 text-secondary-400" />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-primary-300 uppercase tracking-widest leading-none mb-1">Zadzwoń do nas (CH)</p>
                        <p className="font-semibold text-white">+41 76 237 33 01</p>
                      </div>
                    </a>

                    <a href="mailto:mariusz.sokolowski@fgfalke.eu" className="flex items-center space-x-3.5 group text-sm text-primary-200 hover:text-white transition-colors">
                      <div className="bg-white/5 p-2.5 rounded-xl border border-white/10 group-hover:bg-white/10 shrink-0">
                        <Mail className="w-5 h-5 text-secondary-400" />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-primary-300 uppercase tracking-widest leading-none mb-1">Napisz e-mail</p>
                        <p className="font-semibold text-white break-all">mariusz.sokolowski@fgfalke.eu</p>
                      </div>
                    </a>

                    <div className="flex items-center space-x-3.5 text-sm text-primary-200">
                      <div className="bg-white/5 p-2.5 rounded-xl border border-white/10 shrink-0">
                        <MapPin className="w-5 h-5 text-secondary-400" />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-primary-300 uppercase tracking-widest leading-none mb-1">Siedziba PL</p>
                        <p className="font-semibold text-white">Szczurowa, małopolskie</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Languages badging */}
                <div className="pt-6 border-t border-white/10 space-y-3">
                  <p className="text-xs font-extrabold uppercase tracking-wider text-primary-300">Obsługa w językach:</p>
                  <div className="flex flex-wrap gap-2.5">
                    {languages.map((lang, index) => (
                      <span 
                        key={index} 
                        className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-full bg-white/5 border border-white/10 text-white shadow-sm"
                      >
                        <span>{lang.flag}</span>
                        <span>{lang.name}</span>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right Panel - Premium Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="lg:col-span-7"
            >
              <form
                id="form"
                name="contact"
                method="POST"
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                onSubmit={handleSubmit}
                className="bg-white rounded-3xl p-8 md:p-10 border border-slate-100 shadow-xl relative space-y-6"
              >
                <input type="hidden" name="form-name" value="contact" />
                <input type="hidden" name="bot-field" />
                
                <h3 className="text-2xl font-bold font-display text-slate-900 leading-tight">Wyślij Formularz Zapytania</h3>
                <p className="text-slate-500 text-sm leading-relaxed">
                  Podaj szczegóły frachtu lub zakresu wymaganej pomocy (CKZ), a nasz opiekun skontaktuje się z Tobą w celu sfinalizowania kalkulacji.
                </p>

                {/* Grid Inputs */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-1.5">
                    <label htmlFor="name" className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                      Imię i nazwisko / Firma *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="np. Jan Kowalski"
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none bg-slate-50/50 text-sm text-slate-800 placeholder-slate-400"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label htmlFor="email" className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                      Adres E-mail *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="np. nazwa@firma.pl"
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none bg-slate-50/50 text-sm text-slate-800 placeholder-slate-400"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-1.5">
                    <label htmlFor="phone" className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                      Numer Telefonu
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="np. +48 600 000 000"
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none bg-slate-50/50 text-sm text-slate-800 placeholder-slate-400"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label htmlFor="company" className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                      Nazwa Firmy (NIP)
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      placeholder="opcjonalnie"
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none bg-slate-50/50 text-sm text-slate-800 placeholder-slate-400"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="subject" className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                    Temat Zapytania *
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none bg-slate-50/50 text-sm text-slate-800 placeholder-slate-400 cursor-pointer"
                  >
                    <option value="">Wybierz główną kategorię</option>
                    <option value="ckz">Udostępnienie Certyfikatu CKZ</option>
                    <option value="transport">Transport Międzynarodowy FTL/LTL</option>
                    <option value="logistyka">Doradztwo Spedycyjne & Logistyka</option>
                    <option value="kierowca">Rozliczanie czasu pracy kierowców</option>
                    <option value="inne">Inne zapytanie</option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="message" className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                    Opis Potrzeb Transportowych *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    placeholder="Wpisz tutaj dane ładunku, trasę początkową i docelową lub informacje o posiadanej flocie..."
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none bg-slate-50/50 text-sm text-slate-800 placeholder-slate-400"
                  />
                </div>

                {/* Form States */}
                {isSubmitted ? (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center space-x-3 text-emerald-700 bg-emerald-50 border border-emerald-100 p-4 rounded-xl text-sm"
                  >
                    <CheckCircle className="w-5 h-5 shrink-0" />
                    <span>Dziękujemy! Wiadomość została poprawnie wysłana. Odpowiemy w ciągu 24 godzin.</span>
                  </motion.div>
                ) : (
                  <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <span className="text-[10px] text-slate-450 flex items-center gap-1.5 leading-normal">
                      <Lock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      Twoje dane są bezpieczne i przetwarzane wyłącznie w celu wyceny.
                    </span>
                    <button
                      type="submit"
                      className="btn-primary py-3.5 px-8 text-sm font-bold w-full sm:w-auto inline-flex items-center justify-center shrink-0"
                    >
                      <Send className="mr-2 w-4 h-4 shrink-0" />
                      <span>Wyślij Wiadomość</span>
                    </button>
                  </div>
                )}
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Contact
